import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Trash2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { api, errMsg, toList, avatarSrc } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Btn, PillTabs, StatTile, TAGS, height, fmt, shortDate, positionsOf } from "../../components/app/kit";
import { StatsPanel } from "../../components/app/StatsPanel";
import { CombinePanel } from "../../components/app/CombinePanel";
import { ThreadView } from "../../components/app/ThreadView";
import { ProspectCardButton } from "../../components/app/ProspectCardButton";
import { HighlightList } from "../../components/app/Highlights";
import { Archetypes } from "../../components/app/Archetypes";
import { FollowTeamButton } from "./RecruiterSchedule";
import { ShotChart } from "../../components/app/ShotChart";
import { TradingCard } from "../../components/app/TradingCard";
import { BenchmarkRank } from "../../components/app/BenchmarkRank";

const Notes = ({ pid }) => {
  const { data, loading, error, reload } = useApi(`/recruiter/players/${pid}/notes`);
  const [body, setBody] = useState("");
  const notes = toList(data, ["notes"]);
  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try { await api.post(`/recruiter/players/${pid}/notes`, { body: body.trim() }); setBody(""); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/recruiter/notes/${id}`); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Panel>
      <h3 className="mb-1 font-display text-xl font-bold uppercase text-white">Private notes</h3>
      <p className="mb-4 text-sm text-slate-400">Only you see these. The player's coach sees notes as read-only.</p>
      <form onSubmit={add} className="no-print mb-4 flex flex-col gap-2 sm:flex-row">
        <textarea data-testid="note-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Add a scouting note…" className="flex-1 rounded-xl border border-[#1E293B] bg-[#0A0E1A] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#39FF14]" />
        <Btn data-testid="note-add-button" type="submit" disabled={!body.trim()}>Add note</Btn>
      </form>
      {loading ? <Loader /> : error ? <ErrorNote error={error} /> : !notes.length ? <Empty title="No notes yet" /> : (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} data-testid="note-item" className="flex gap-3 rounded-xl border border-[#1E293B] p-4">
              <div className="flex-1"><p className="whitespace-pre-wrap text-sm text-slate-100">{n.body}</p><p className="mt-2 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{shortDate(n.created_at)}</p></div>
              <button data-testid={`note-delete-${n.id}`} onClick={() => del(n.id)} className="no-print self-start rounded-full p-2 text-slate-400 hover:text-red-200" aria-label="Delete note"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

const Messages = ({ pid }) => {
  const { data, reload } = useApi(`/recruiter/messages/thread?player_id=${pid}`);
  const send = async (body) => { await api.post(`/recruiter/players/${pid}/message`, { body }); reload(); };
  return <ThreadView messages={toList(data, ["messages"])} mineSender="recruiter" onSend={send} title="Message the coach" subtitle={data?.team_name} />;
};

export default function Prospect() {
  const { playerId } = useParams();
  const { data, loading, error, reload } = useApi(`/recruiter/players/${playerId}`);
  const [tab, setTab] = useState("Stats");
  if (loading && !data) return <Loader />;
  if (error) return <ErrorNote error={error} />;
  const p = data?.player ?? {};
  const links = toList(data?.highlight_links ?? p.highlight_links).map((l) => (typeof l === "string" ? l : l?.url)).filter(Boolean);

  const setTag = async (tag) => {
    try { await api.put(`/recruiter/players/${playerId}/tag`, { tag: tag || null }); toast.success(tag ? `Tagged ${tag}` : "Tag cleared"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Link to="/recruiter" data-testid="back-to-board" className="no-print mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><ArrowLeft className="h-4 w-4" />Board</Link>
      <PageHeader eyebrow={`#${p.jersey ?? "–"} · ${positionsOf(p)} · ${data?.team_name ?? ""}`} title={p.name ?? "Prospect"}>
        <select data-testid="prospect-tag-select" value={data?.tag ?? ""} onChange={(e) => setTag(e.target.value)} className="rounded-full border border-[#1E293B] bg-[#162032] px-4 py-2.5 text-sm text-white">
          <option value="">No tag</option>
          {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <ProspectCardButton data={data} />
        <FollowTeamButton teamId={data?.team_id ?? p.team_id} teamName={data?.team_name} />
        <Btn variant="ghost" data-testid="print-dossier-button" onClick={() => window.print()}><Printer className="h-4 w-4" />Print dossier</Btn>
      </PageHeader>
      <BenchmarkRank className="-mt-4 mb-6" player={p} bests={data?.personal_bests} />
      <div className="mb-6 flex items-center gap-4">
        <TradingCard p={p} teamName={data?.team_name} photo={avatarSrc(data?.team_id ?? p.team_id, p)} season={data?.season} perGame={data?.per_game ?? data?.season?.per_game} />
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Class" value={p.grad_year ?? "—"} accent /><StatTile label="Height" value={height(p.height_in)} />
          <StatTile label="Weight" value={p.weight_lb ? `${p.weight_lb} lb` : "—"} /><StatTile label="GPA" value={fmt(p.gpa)} />
        </div>
      </div>
      <Archetypes p={p} />
      {links.length > 0 && tab !== "Highlights" && (
        <button data-testid="watch-highlights-button" onClick={() => setTab("Highlights")} className="no-print mb-6 inline-flex items-center gap-2 rounded-full border border-[#39FF14]/40 px-4 py-2 text-sm text-[#39FF14] transition-colors hover:bg-[#39FF14]/10">
          <PlayCircle className="h-4 w-4" />Watch highlights ({links.length})
        </button>
      )}
      <PillTabs tabs={["Stats", "Shot Chart", "Combine", "Highlights", "Notes", "Messages"]} value={tab} onChange={setTab} />
      {tab === "Shot Chart" && <ShotChart pid={playerId} />}
      {tab === "Highlights" && <HighlightList links={links} emptyText="This player's coach hasn't added highlight videos yet." />}
      {tab === "Stats" && <StatsPanel season={data?.season} perGame={data?.per_game} zonePct={data?.zone_pct} />}
      {tab === "Combine" && <CombinePanel player={p} latest={data?.latest_combine} bests={data?.personal_bests} comparison={data?.comparison} />}
      {tab === "Notes" && <Notes pid={playerId} />}
      {tab === "Messages" && <Messages pid={playerId} />}
    </div>
  );
}
